import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Avatar,
  Row,
  Col,
  message,
  Spin,
  Select,
  Switch,
  Tabs,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  SecurityScanOutlined,
} from '@ant-design/icons';
import { profileApi } from '../services/profileApi';
import useStore from '../store/useStore';

const { Title, Text } = Typography;
const { Option } = Select;

const Profile = () => {
  // NEW PROFILE COMPONENT V2 - Fixed null reference errors
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const { user } = useStore();

  const [userProfile, setUserProfile] = useState(null);

  console.log('🔄 NEW Profile component loaded - V2');

  // Load user profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setProfileLoading(true);
        
        // Try to get profile from API
        try {
          const profile = await profileApi.getProfile();
          setUserProfile(profile);
          
          // Populate form with profile data
          form.setFieldsValue({
            firstName: profile.firstName || profile.name?.split(' ')[0] || '',
            lastName: profile.lastName || profile.name?.split(' ').slice(1).join(' ') || '',
            email: profile.email || '',
            phone: profile.phone || '',
            department: profile.department || 'General',
            timezone: profile.timezone || 'UTC',
            language: profile.language || 'en',
            emailNotifications: profile.notifications?.email !== false,
            pushNotifications: profile.notifications?.push !== false,
            smsNotifications: profile.notifications?.sms === true,
          });
        } catch (apiError) {
          console.log('Profile API not available, using basic user data');
          
          // Fallback to basic user data from store
          if (user) {
            const basicProfile = {
              id: user.id,
              email: user.email,
              name: user.name,
              firstName: user.name?.split(' ')[0] || '',
              lastName: user.name?.split(' ').slice(1).join(' ') || '',
              role: 'User',
              department: 'General',
              timezone: 'UTC',
              language: 'en',
              notifications: { email: true, push: true, sms: false },
              twoFactorEnabled: false,
            };
            setUserProfile(basicProfile);
            
            // Populate form with basic data
            form.setFieldsValue({
              firstName: basicProfile.firstName,
              lastName: basicProfile.lastName,
              email: basicProfile.email,
              phone: '',
              department: 'General',
              timezone: 'UTC',
              language: 'en',
              emailNotifications: true,
              pushNotifications: true,
              smsNotifications: false,
            });
          }
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
        message.error('Failed to load profile data');
      } finally {
        setProfileLoading(false);
      }
    };

    if (user) {
      loadProfile();
    }
  }, [user, form]);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const updatedProfile = await profileApi.updateProfile({
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        department: values.department,
        timezone: values.timezone,
        language: values.language,
        notifications: {
          email: values.emailNotifications,
          push: values.pushNotifications,
          sms: values.smsNotifications,
        },
      });
      
      setUserProfile(updatedProfile);
      setEditing(false);
      message.success('Profile updated successfully');
    } catch (error) {
      console.error('Profile update error:', error);
      message.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (values) => {
    setLoading(true);
    try {
      await profileApi.updatePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      
      passwordForm.resetFields();
      message.success('Password changed successfully');
    } catch (error) {
      console.error('Password change error:', error);
      message.error('Failed to change password. Please check your current password.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while fetching profile data
  if (profileLoading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <Title level={2} className="mb-0">
            <UserOutlined className="mr-2" />
            Profile
          </Title>
          <Text type="secondary">
            Manage your account settings and preferences
          </Text>
        </div>
        <Card>
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
            <Text className="ml-3">Loading profile data...</Text>
          </div>
        </Card>
      </div>
    );
  }

  // If no profile data, show error
  if (!userProfile) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <Title level={2} className="mb-0">
            <UserOutlined className="mr-2" />
            Profile
          </Title>
          <Text type="secondary">
            Manage your account settings and preferences
          </Text>
        </div>
        <Card>
          <div className="text-center py-12">
            <Text type="secondary">Unable to load profile data. Please try refreshing the page.</Text>
          </div>
        </Card>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined /> Profile Information
        </span>
      ),
      children: (
        <div className="space-y-6">
          {/* Profile Header */}
          <div className="flex items-center space-x-6 mb-6">
            <div className="relative">
              <Avatar size={100} icon={<UserOutlined />} />
            </div>
            <div>
              <Title level={3} className="mb-0">
                {userProfile.name || `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() || 'User'}
              </Title>
              <Text type="secondary" className="text-lg">
                {userProfile.role || 'User'}
              </Text>
              <br />
              <Text type="secondary">{userProfile.department || 'General'} • QQINV Co.</Text>
            </div>
            <div className="ml-auto">
              <Button
                type={editing ? 'default' : 'primary'}
                icon={editing ? <SaveOutlined /> : <EditOutlined />}
                onClick={() => {
                  if (editing) {
                    form.submit();
                  } else {
                    setEditing(true);
                  }
                }}
                loading={loading}
              >
                {editing ? 'Save Changes' : 'Edit Profile'}
              </Button>
              {editing && (
                <Button
                  className="ml-2"
                  onClick={() => {
                    setEditing(false);
                    form.resetFields();
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>

          {/* Profile Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            disabled={!editing}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: 'Please enter your first name' }]}
                >
                  <Input prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: 'Please enter your last name' }]}
                >
                  <Input prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input prefix={<MailOutlined />} disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Phone" name="phone">
                  <Input prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Department" name="department">
                  <Select>
                    <Option value="Operations">Operations</Option>
                    <Option value="Sales">Sales</Option>
                    <Option value="Marketing">Marketing</Option>
                    <Option value="Finance">Finance</Option>
                    <Option value="IT">IT</Option>
                    <Option value="HR">HR</Option>
                    <Option value="General">General</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Timezone" name="timezone">
                  <Select>
                    <Option value="UTC">UTC</Option>
                    <Option value="America/New_York">Eastern Time</Option>
                    <Option value="America/Chicago">Central Time</Option>
                    <Option value="America/Denver">Mountain Time</Option>
                    <Option value="America/Los_Angeles">Pacific Time</Option>
                    <Option value="Asia/Kolkata">India Standard Time</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Language" name="language">
                  <Select>
                    <Option value="en">English</Option>
                    <Option value="es">Spanish</Option>
                    <Option value="fr">French</Option>
                    <Option value="de">German</Option>
                    <Option value="zh">Chinese</Option>
                    <Option value="hi">Hindi</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Notification Preferences */}
            <div className="space-y-4">
              <Title level={4}>Notification Preferences</Title>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>Email Notifications</Text>
                    <br />
                    <Text type="secondary">Receive notifications via email</Text>
                  </div>
                  <Form.Item name="emailNotifications" valuePropName="checked" className="mb-0">
                    <Switch />
                  </Form.Item>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>Push Notifications</Text>
                    <br />
                    <Text type="secondary">Receive push notifications in browser</Text>
                  </div>
                  <Form.Item name="pushNotifications" valuePropName="checked" className="mb-0">
                    <Switch />
                  </Form.Item>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>SMS Notifications</Text>
                    <br />
                    <Text type="secondary">Receive notifications via SMS</Text>
                  </div>
                  <Form.Item name="smsNotifications" valuePropName="checked" className="mb-0">
                    <Switch />
                  </Form.Item>
                </div>
              </div>
            </div>
          </Form>
        </div>
      ),
    },
    {
      key: 'security',
      label: (
        <span>
          <SecurityScanOutlined /> Security
        </span>
      ),
      children: (
        <div className="space-y-6">
          <div>
            <Title level={4}>
              <LockOutlined className="mr-2" />
              Change Password
            </Title>
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
              className="max-w-md"
            >
              <Form.Item
                label="Current Password"
                name="currentPassword"
                rules={[{ required: true, message: 'Please enter your current password' }]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                label="New Password"
                name="newPassword"
                rules={[
                  { required: true, message: 'Please enter a new password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                ]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                label="Confirm New Password"
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm your new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2} className="mb-0">
          <UserOutlined className="mr-2" />
          Profile
        </Title>
        <Text type="secondary">
          Manage your account settings and preferences
        </Text>
      </div>

      <Card>
        <Tabs items={tabItems} />
      </Card>
    </div>
  );
};

export default Profile;
